import { Fragment, ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";

import { TableEmptyRow } from "@/components/Table/TableEmptyRow";

import { MetricHelp, MetricTitle } from "./MetricHelp";
import { formatCount, formatDate, formatWindow } from "../utils/featureUsage";

import type {
	ProductUsageAreaGroup,
	ProductUsageFeatureRef,
	ProductUsageFreshness,
	ProductUsageCohort,
	ProductUsageDistinctUsersCoverage,
} from "@signalist/shared-types";

export function UsageStatus({
	isLoading,
	isError,
	data,
	retry,
}: {
	isLoading?: boolean;
	isError?: boolean;
	data?: { lastRolledUpDate: string | null };
	retry?: () => void;
}) {
	const { t } = useTranslation();

	if (isError) {
		return (
			<div role="alert" className="py-8 text-status-error">
				{t("featureUsage.error")}
				{retry && (
					<button className="ml-3 cursor-pointer underline" type="button" onClick={retry}>
						{t("featureUsage.retry")}
					</button>
				)}
			</div>
		);
	}

	if (isLoading || !data) {
		return (
			<div role="status" className="py-8 text-text-meta">
				{t("featureUsage.loading")}
			</div>
		);
	}

	if (data.lastRolledUpDate !== null) {
		return null;
	}

	return (
		<table className="w-full">
			<tbody>
				<TableEmptyRow
					colSpan={1}
					noDataText={
						<div role="status">
							<div>{t("featureUsage.empty")}</div>
							<p className="mt-3 text-text-meta">{t("featureUsage.capture")}</p>
							<p className="mt-2 text-xs text-text-meta">{t("featureUsage.rollup")}</p>
						</div>
					}
				/>
			</tbody>
		</table>
	);
}
export function UsageSummary({
	data,
}: {
	data: ProductUsageFreshness & {
		cohort?: ProductUsageCohort;
	} & Partial<ProductUsageDistinctUsersCoverage>;
}) {
	const { t } = useTranslation();

	return (
		<div className="my-4 space-y-2 text-xs text-text-meta">
			<div className="flex flex-wrap items-center gap-2">
				<span>{t("featureUsage.window", { range: formatWindow(data.window) })}</span>
				<MetricHelp
					label={t("featureUsage.metrics.window")}
					definition={t("featureUsage.help.window")}
				/>
				<span>{t("featureUsage.staffExcluded")}</span>
				{data.lastRolledUpDate && (
					<span>{t("featureUsage.rolledUp", { date: formatDate(data.lastRolledUpDate) })}</span>
				)}
			</div>
			{data.cohort && (
				<h2 className="text-sm font-semibold text-text">
					{data.cohort.description}
					<MetricHelp
						label={t("featureUsage.metrics.cohort")}
						definition={t("featureUsage.help.cohort")}
					/>
				</h2>
			)}
			{data.distinctUsersDates && (
				<div>
					{t("featureUsage.coverage", { count: data.distinctUsersDays })}
					<MetricHelp
						label={t("featureUsage.metrics.coverage")}
						definition={t("featureUsage.help.coverage", {
							dates: data.distinctUsersDates.join(", ") || t("featureUsage.notMeasured"),
						})}
					/>
				</div>
			)}
		</div>
	);
}
export function FeatureCell({ feature }: { feature: ProductUsageFeatureRef }) {
	return (
		<div>
			<div className="font-medium text-text">{feature.label}</div>
			<div className="mt-1 text-xs text-text-meta">{feature.key}</div>
		</div>
	);
}
export interface UsageColumn<Row> {
	key: string;
	title: ReactNode;
	render: (row: Row) => ReactNode;
	sort?: () => void;
	direction?: "asc" | "desc";
}
export function UsageTable<Row extends ProductUsageFeatureRef>({
	areas,
	columns,
	label,
}: {
	areas: readonly ProductUsageAreaGroup<Row>[];
	columns: UsageColumn<Row>[];
	label: string;
}) {
	const { t } = useTranslation();
	const [page, setPage] = useState(0);
	const count = areas.reduce((sum, group) => sum + group.features.length, 0);
	const pages = Math.max(1, Math.ceil(count / 10));
	const current = Math.min(page, pages - 1);
	let offset = 0;

	return (
		<>
			<div className="overflow-x-auto">
				<table aria-label={label} className="w-full text-left text-sm">
					<thead>
						<tr>
							{columns.map((column) => (
								<th
									key={column.key}
									aria-sort={
										column.direction === "asc"
											? "ascending"
											: column.direction === "desc"
												? "descending"
												: undefined
									}
									className="border-b border-border-light px-3 py-3 font-normal text-text-meta"
								>
									{column.title}
									{column.sort && (
										<button
											aria-label={t("featureUsage.sort", {
												column: t(`featureUsage.metrics.${column.key}`),
											})}
											className="ml-1 cursor-pointer"
											type="button"
											onClick={column.sort}
										>
											↕
										</button>
									)}
								</th>
							))}
						</tr>
					</thead>
					<tbody>
						{count === 0 && (
							<TableEmptyRow colSpan={columns.length} noDataText={t("featureUsage.noMatches")} />
						)}
						{areas.map((group) => {
							const start = offset;

							offset += group.features.length;
							const visible = group.features.slice(
								Math.max(0, current * 10 - start),
								Math.max(0, (current + 1) * 10 - start)
							);

							if (!visible.length) {
								return null;
							}

							return (
								<Fragment key={group.area}>
									<tr className="bg-brand-white">
										<th
											className="px-3 py-2 text-xs text-text-meta"
											colSpan={columns.length}
											scope="rowgroup"
										>
											{group.area}
										</th>
									</tr>
									{visible.map((row) => (
										<tr key={row.key}>
											{columns.map((column) => (
												<td
													key={column.key}
													className="border-b border-border-light px-3 py-3 align-top"
												>
													{column.render(row)}
												</td>
											))}
										</tr>
									))}
								</Fragment>
							);
						})}
					</tbody>
				</table>
			</div>
			{pages > 1 && (
				<div className="mt-3 flex items-center justify-end gap-3 text-xs text-text-meta">
					<button
						className="cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
						disabled={current === 0}
						type="button"
						onClick={() => setPage(current - 1)}
					>
						{t("featureUsage.previous")}
					</button>
					<span>
						{t("featureUsage.page", { page: formatCount(current + 1), pages: formatCount(pages) })}
					</span>
					<MetricTitle metric="pages" />
					<button
						className="cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
						disabled={current + 1 === pages}
						type="button"
						onClick={() => setPage(current + 1)}
					>
						{t("featureUsage.next")}
					</button>
				</div>
			)}
		</>
	);
}
